// Version Manager for PWA Cache Busting and Update Detection
class VersionManager {
  constructor() {
    this.currentVersion = null;
    this.latestVersion = null;
    this.versionCheckInterval = null;
    this.eventSlug = this.getEventSlug();
    this.versionKey = this.eventSlug
      ? `pwa_version_${this.eventSlug}`
      : "pwa_version";
    this.init();
  }

  getEventSlug() {
    // Extract event slug from URL path /e/{slug}/...
    const match = window.location.pathname.match(/\/e\/([^\/]+)/);
    return match ? match[1] : null;
  }

  init() {
    this.loadStoredVersion();
    this.restoreAuthenticationData(); // Restore any preserved auth data on startup
    this.checkForUpdates();
    this.startPeriodicVersionCheck();
  }

  loadStoredVersion() {
    try {
      const stored = localStorage.getItem(this.versionKey);
      this.currentVersion = stored ? JSON.parse(stored) : null;
    } catch (error) {
      this.currentVersion = null;
    }
  }

  async checkForUpdates() {
    try {
      // Fetch the latest version info
      const response = await fetch("/e/version.json?" + Date.now(), {
        cache: "no-cache",
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
        },
      });

      if (!response.ok) {
        return;
      }

      const versionInfo = await response.json();
      this.latestVersion = versionInfo;

      // Compare versions
      this.compareVersions();
    } catch (error) {
      // Silent fail
    }
  }

  compareVersions() {
    if (!this.latestVersion) {
      return;
    }

    // If there's no current version, this is the first load - store the latest version
    if (!this.currentVersion) {
      this.storeVersion(this.latestVersion);
      return;
    }

    const currentTimestamp = this.currentVersion.timestamp;
    const latestTimestamp = this.latestVersion.timestamp;

    if (latestTimestamp > currentTimestamp) {
      this.handleVersionUpdate();
    }
  }

  handleVersionUpdate() {
    // Notify the app about the update (don't store yet - wait for user to apply update)
    this.notifyUpdateAvailable();

    // Force service worker update check
    this.forceServiceWorkerUpdate();
  }

  storeVersion(versionInfo) {
    try {
      localStorage.setItem(this.versionKey, JSON.stringify(versionInfo));
      this.currentVersion = versionInfo;
    } catch (error) {
      // Silent fail
    }
  }

  notifyUpdateAvailable() {
    // Dispatch custom event for update notification
    const event = new CustomEvent("pwa-version-update", {
      detail: {
        currentVersion: this.currentVersion,
        latestVersion: this.latestVersion,
        message: "A new version of the app is available!",
      },
    });

    window.dispatchEvent(event);
  }

  async forceServiceWorkerUpdate() {
    if ("serviceWorker" in navigator) {
      try {
        // Get all service worker registrations
        const registrations = await navigator.serviceWorker.getRegistrations();

        for (const registration of registrations) {
          // Force update the service worker
          await registration.update();

          // If there's a waiting service worker, activate it
          if (registration.waiting) {
            registration.waiting.postMessage({ type: "SKIP_WAITING" });
          }
        }
      } catch (error) {}
    }
  }

  startPeriodicVersionCheck() {
    // Check for updates every 5 minutes
    this.versionCheckInterval = setInterval(() => {
      this.checkForUpdates();
    }, 5 * 60 * 1000);

    // Also check when the page becomes visible
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) {
        this.checkForUpdates();
      }
    });
  }

  // Force update for critical updates
  async forceUpdate() {
    try {
      // Store the new version BEFORE clearing caches
      if (this.latestVersion) {
        this.storeVersion(this.latestVersion);
      }

      // Preserve authentication data before clearing caches
      this.preserveAuthenticationData();

      // Clear all caches
      await this.clearAllCaches();

      // Force service worker update
      await this.forceServiceWorkerUpdate();

      // Reload the page immediately
      window.location.reload();
    } catch (error) {
      // If anything fails, just reload
      window.location.reload();
    }
  }

  async clearAllCaches() {
    if ("caches" in window) {
      try {
        // Preserve authentication data before clearing caches
        this.preserveAuthenticationData();

        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map((cacheName) => caches.delete(cacheName))
        );

        // Restore authentication data after clearing caches
        this.restoreAuthenticationData();
      } catch (error) {}
    }
  }

  preserveAuthenticationData() {
    try {
      // Find all attendee session keys in localStorage
      const authData = {};
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith("attendee_session_")) {
          authData[key] = localStorage.getItem(key);
        }
      }

      // eslint-disable-next-line no-unused-vars
      // Store preserved data in sessionStorage temporarily
      sessionStorage.setItem("preserved_auth_data", JSON.stringify(authData));
    } catch (error) {}
  }

  restoreAuthenticationData() {
    try {
      const preservedData = sessionStorage.getItem("preserved_auth_data");
      if (preservedData) {
        const authData = JSON.parse(preservedData);

        // Restore each auth session
        Object.entries(authData).forEach(([key, value]) => {
          localStorage.setItem(key, value);
        });

        // Clean up temporary storage
        sessionStorage.removeItem("preserved_auth_data");

        // Dispatch event to notify AttendeeContext of restored sessions
        window.dispatchEvent(new CustomEvent("attendee_session_updated"));
      }
    } catch (error) {}
  }

  // Get current version info
  getCurrentVersion() {
    return this.currentVersion;
  }

  // Get latest version info
  getLatestVersion() {
    return this.latestVersion;
  }

  // Check if update is available
  isUpdateAvailable() {
    if (!this.currentVersion || !this.latestVersion) {
      return false;
    }
    return this.latestVersion.timestamp > this.currentVersion.timestamp;
  }

  destroy() {
    if (this.versionCheckInterval) {
      clearInterval(this.versionCheckInterval);
      this.versionCheckInterval = null;
    }
  }
}

// Create global instance
const versionManager = new VersionManager();

export default versionManager;
